import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  LayoutDashboard, Plus, Trash2, Edit, Save, GripVertical, X, Check,
  ListTodo, Target, DollarSign, Users, Calendar, Activity, BarChart3,
  PieChart, Clock, AlertTriangle, Bell, Link, Briefcase, Headphones,
  Megaphone, Wallet, Settings, RefreshCw, Loader2, Copy, Share2,
  ChevronDown, Grid3X3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Widget icon mapping
const WIDGET_ICONS = {
  my_tasks: ListTodo,
  my_deals: Target,
  my_tickets: Headphones,
  recent_activity: Activity,
  calendar_events: Calendar,
  pipeline_overview: BarChart3,
  revenue_chart: DollarSign,
  team_performance: Users,
  department_kpis: Briefcase,
  company_metrics: PieChart,
  sales_leaderboard: Target,
  ticket_stats: Headphones,
  marketing_performance: Megaphone,
  hr_overview: Users,
  announcements: Bell,
  quick_links: Link,
  overdue_tasks: AlertTriangle,
  pending_approvals: Clock,
  financial_summary: Wallet,
  goal_progress: Target
};

// Size options
const SIZE_OPTIONS = [
  { value: 'small', label: 'Small (1x1)', w: 4, h: 2 },
  { value: 'medium', label: 'Medium (2x1)', w: 6, h: 3 },
  { value: 'large', label: 'Large (3x1)', w: 8, h: 3 },
  { value: 'xlarge', label: 'Extra Large (Full)', w: 12, h: 4 }
];

// Widget Card Component
const WidgetCard = ({ widget, onRemove, onResize, isEditing }) => {
  const Icon = WIDGET_ICONS[widget.widget_key] || LayoutDashboard;
  
  return (
    <Card className={`relative h-full ${isEditing ? 'border-dashed border-2 border-blue-300' : ''}`}>
      {isEditing && (
        <div className="absolute top-2 right-2 flex items-center gap-1 z-10">
          <Select 
            value={widget.size || 'medium'}
            onValueChange={(size) => onResize(widget.widget_key, size)}
          >
            <SelectTrigger className="h-7 w-24 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SIZE_OPTIONS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => onRemove(widget.widget_key)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      <CardContent className="pt-6 h-full flex flex-col">
        <div className="flex items-center gap-3 mb-3">
          {isEditing && <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />}
          <Icon className="h-5 w-5 text-muted-foreground" />
          <span className="font-medium">{widget.name}</span>
        </div>
        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
          {isEditing ? (
            <span>Widget preview</span>
          ) : (
            <span>Loading widget data...</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const CustomDashboardBuilderPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Data states
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [widgetCategories, setWidgetCategories] = useState([]);
  const [presets, setPresets] = useState([]);
  const [userDashboards, setUserDashboards] = useState([]);
  const [currentDashboard, setCurrentDashboard] = useState(null);
  
  // UI states
  const [activeTab, setActiveTab] = useState('builder');
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);
  const [createDashboardOpen, setCreateDashboardOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newDashboardName, setNewDashboardName] = useState('');
  const [newDashboardLevel, setNewDashboardLevel] = useState('personal');

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [widgetsRes, presetsRes, dashboardsRes] = await Promise.all([
        axios.get(`${API}/api/dashboard-config/widgets`, { headers }),
        axios.get(`${API}/api/dashboard-config/presets`, { headers }),
        axios.get(`${API}/api/dashboard-config/dashboards`, { headers })
      ]);

      setAvailableWidgets(widgetsRes.data.widgets || []);
      setWidgetCategories(['all', ...(widgetsRes.data.categories || [])]);
      setPresets(presetsRes.data.presets || []);
      setUserDashboards(dashboardsRes.data.dashboards || []);

      // Set default dashboard if none selected
      if (!currentDashboard && dashboardsRes.data.dashboards?.length > 0) {
        setCurrentDashboard(dashboardsRes.data.dashboards[0]);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard config:', err);
      toast.error('Failed to load dashboard configuration');
    } finally {
      setLoading(false);
    }
  }, [currentDashboard]);

  useEffect(() => {
    fetchData();
  }, []);

  const createDashboardFromPreset = async (presetKey) => {
    try {
      setSaving(true);
      const res = await axios.post(
        `${API}/api/dashboard-config/presets/${presetKey}/apply`,
        {},
        { headers }
      );
      toast.success('Dashboard created from preset');
      setCurrentDashboard(res.data.dashboard);
      fetchData();
    } catch (err) {
      toast.error('Failed to create dashboard');
    } finally {
      setSaving(false);
    }
  };

  const createNewDashboard = async () => {
    if (!newDashboardName.trim()) {
      toast.error('Please enter a dashboard name');
      return;
    }

    try {
      setSaving(true);
      const res = await axios.post(
        `${API}/api/dashboard-config/dashboards`,
        null,
        { 
          headers,
          params: {
            name: newDashboardName,
            level: newDashboardLevel,
            widgets: []
          }
        }
      );
      toast.success('Dashboard created');
      setCurrentDashboard(res.data.dashboard);
      setCreateDashboardOpen(false);
      setNewDashboardName('');
      fetchData();
    } catch (err) {
      toast.error('Failed to create dashboard');
    } finally {
      setSaving(false);
    }
  };

  const saveDashboard = async () => {
    if (!currentDashboard) return;

    try {
      setSaving(true);
      await axios.put(
        `${API}/api/dashboard-config/dashboards/${currentDashboard.id}`,
        null,
        {
          headers,
          params: {
            widgets: JSON.stringify(currentDashboard.widgets)
          }
        }
      );
      toast.success('Dashboard saved');
      setIsEditing(false);
    } catch (err) {
      toast.error('Failed to save dashboard');
    } finally {
      setSaving(false);
    }
  };

  const deleteDashboard = async (dashboardId) => {
    if (!window.confirm('Delete this dashboard?')) return;

    try {
      await axios.delete(`${API}/api/dashboard-config/dashboards/${dashboardId}`, { headers });
      toast.success('Dashboard deleted');
      if (currentDashboard?.id === dashboardId) {
        setCurrentDashboard(null);
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to delete dashboard');
    }
  };

  const addWidget = (widgetKey) => {
    if (!currentDashboard) return;

    const widget = availableWidgets.find(w => w.key === widgetKey);
    if (!widget) return;

    // Check if widget already exists
    if (currentDashboard.widgets?.some(w => w.widget_key === widgetKey)) {
      toast.error('Widget already added');
      return;
    }

    const sizeInfo = SIZE_OPTIONS.find(s => s.value === (widget.default_size || 'medium'));
    const newWidget = {
      widget_key: widgetKey,
      name: widget.name,
      size: widget.default_size || 'medium',
      position: { x: 0, y: 0, w: sizeInfo?.w || 6, h: sizeInfo?.h || 3 }
    };

    setCurrentDashboard(prev => ({
      ...prev,
      widgets: [...(prev.widgets || []), newWidget]
    }));

    setAddWidgetOpen(false);
    toast.success('Widget added');
  };

  const removeWidget = (widgetKey) => {
    if (!currentDashboard) return;

    setCurrentDashboard(prev => ({
      ...prev,
      widgets: (prev.widgets || []).filter(w => w.widget_key !== widgetKey)
    }));
  };

  const resizeWidget = (widgetKey, size) => {
    if (!currentDashboard) return;

    const sizeInfo = SIZE_OPTIONS.find(s => s.value === size);
    
    setCurrentDashboard(prev => ({
      ...prev,
      widgets: (prev.widgets || []).map(w => 
        w.widget_key === widgetKey 
          ? { ...w, size, position: { ...w.position, w: sizeInfo?.w || 6, h: sizeInfo?.h || 3 } }
          : w
      )
    }));
  };

  const getWidgetIcon = (key) => {
    const Icon = WIDGET_ICONS[key] || LayoutDashboard;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="custom-dashboard-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Grid3X3 className="h-8 w-8 text-indigo-600" />
            Dashboard Builder
          </h1>
          <p className="text-muted-foreground mt-1">Create and customize your personal dashboards</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setCreateDashboardOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Dashboard
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="builder">Dashboard Builder</TabsTrigger>
          <TabsTrigger value="presets">Templates</TabsTrigger>
          <TabsTrigger value="my_dashboards">My Dashboards</TabsTrigger>
        </TabsList>

        {/* Builder Tab */}
        <TabsContent value="builder" className="space-y-4">
          {currentDashboard ? (
            <>
              {/* Dashboard Header */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <LayoutDashboard className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <h2 className="font-semibold text-lg">{currentDashboard.name}</h2>
                        <p className="text-sm text-muted-foreground capitalize">
                          {currentDashboard.level?.replace('_', ' ')} • {currentDashboard.widgets?.length || 0} widgets
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <Button variant="outline" onClick={() => setIsEditing(false)}>
                            Cancel
                          </Button>
                          <Button onClick={saveDashboard} disabled={saving}>
                            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                            Save
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button variant="outline" onClick={() => setIsEditing(true)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                          <Button variant="outline" onClick={() => setAddWidgetOpen(true)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Widget
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Widgets Grid */}
              {currentDashboard.widgets?.length > 0 ? (
                <div className="grid grid-cols-12 gap-4">
                  {currentDashboard.widgets.map((widget) => {
                    const colSpan = widget.position?.w || 6;
                    return (
                      <div 
                        key={widget.widget_key} 
                        className={`col-span-${Math.min(colSpan, 12)} min-h-[150px]`}
                        style={{ gridColumn: `span ${Math.min(colSpan, 12)}` }}
                      >
                        <WidgetCard
                          widget={widget}
                          onRemove={removeWidget}
                          onResize={resizeWidget}
                          isEditing={isEditing}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Card className="py-16">
                  <CardContent className="text-center">
                    <LayoutDashboard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-semibold text-lg mb-2">No widgets yet</h3>
                    <p className="text-muted-foreground mb-4">Add widgets to customize your dashboard</p>
                    <Button onClick={() => setAddWidgetOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First Widget
                    </Button>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="py-16">
              <CardContent className="text-center">
                <LayoutDashboard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">No dashboard selected</h3>
                <p className="text-muted-foreground mb-4">Create a new dashboard or select from your existing ones</p>
                <div className="flex items-center justify-center gap-3">
                  <Button onClick={() => setCreateDashboardOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Dashboard
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('presets')}>
                    Use a Template
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Presets Tab */}
        <TabsContent value="presets" className="space-y-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {presets.map((preset) => (
              <Card key={preset.key} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">{preset.name}</CardTitle>
                  <CardDescription>{preset.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {preset.widgets?.slice(0, 6).map((w, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {getWidgetIcon(w.widget_key)}
                        <span className="ml-1">{w.widget_key.replace(/_/g, ' ')}</span>
                      </Badge>
                    ))}
                    {preset.widgets?.length > 6 && (
                      <Badge variant="outline" className="text-xs">
                        +{preset.widgets.length - 6} more
                      </Badge>
                    )}
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={() => createDashboardFromPreset(preset.key)}
                    disabled={saving}
                  >
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
                    Use This Template
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* My Dashboards Tab */}
        <TabsContent value="my_dashboards" className="space-y-4">
          {userDashboards.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {userDashboards.map((dashboard) => (
                <Card 
                  key={dashboard.id} 
                  className={`hover:shadow-md transition-shadow cursor-pointer ${
                    currentDashboard?.id === dashboard.id ? 'ring-2 ring-blue-500' : ''
                  }`}
                  onClick={() => { setCurrentDashboard(dashboard); setActiveTab('builder'); }}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{dashboard.name}</CardTitle>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); deleteDashboard(dashboard.id); }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                    <CardDescription className="capitalize">
                      {dashboard.level?.replace('_', ' ')} • {dashboard.widgets?.length || 0} widgets
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1">
                      {dashboard.widgets?.slice(0, 4).map((w, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {w.widget_key?.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                      {dashboard.widgets?.length > 4 && (
                        <Badge variant="outline" className="text-xs">
                          +{dashboard.widgets.length - 4}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-16">
              <CardContent className="text-center">
                <LayoutDashboard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">No dashboards yet</h3>
                <p className="text-muted-foreground mb-4">Create your first custom dashboard</p>
                <Button onClick={() => setCreateDashboardOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Dashboard
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Widget Dialog */}
      <Dialog open={addWidgetOpen} onOpenChange={setAddWidgetOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Widget</DialogTitle>
            <DialogDescription>Select a widget to add to your dashboard</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {widgetCategories.map(cat => (
                <Badge
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  className="cursor-pointer capitalize"
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>

            {/* Widget List */}
            <ScrollArea className="h-[400px]">
              <div className="grid grid-cols-2 gap-3">
                {availableWidgets
                  .filter(w => selectedCategory === 'all' || w.category === selectedCategory)
                  .map((widget) => {
                    const isAdded = currentDashboard?.widgets?.some(w => w.widget_key === widget.key);
                    return (
                      <div
                        key={widget.key}
                        className={`p-4 rounded-lg border cursor-pointer transition-all ${
                          isAdded 
                            ? 'bg-slate-100 opacity-60 cursor-not-allowed' 
                            : 'hover:border-blue-500 hover:bg-blue-50'
                        }`}
                        onClick={() => !isAdded && addWidget(widget.key)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-slate-100 rounded-lg">
                            {getWidgetIcon(widget.key)}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{widget.name}</p>
                            <p className="text-xs text-muted-foreground line-clamp-1">{widget.description}</p>
                          </div>
                          {isAdded && <Check className="h-4 w-4 text-green-500" />}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Dashboard Dialog */}
      <Dialog open={createDashboardOpen} onOpenChange={setCreateDashboardOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Dashboard</DialogTitle>
            <DialogDescription>Set up a new custom dashboard</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Dashboard Name</Label>
              <Input
                value={newDashboardName}
                onChange={(e) => setNewDashboardName(e.target.value)}
                placeholder="e.g., My Sales Dashboard"
              />
            </div>
            <div className="space-y-2">
              <Label>Dashboard Level</Label>
              <Select value={newDashboardLevel} onValueChange={setNewDashboardLevel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="department">Department</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDashboardOpen(false)}>Cancel</Button>
            <Button onClick={createNewDashboard} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create Dashboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomDashboardBuilderPage;
